import { useMemo, useCallback } from 'react';
import { resolveMergeTags } from '@nexmail/shared-util/email-builder';
import { buildBrandResolverContext } from '@/lib/brand-resolver-context';
import { useBrandsControllerGetClientBrandProfile } from '@/apis/backend/generated/brands/brands';
import { useSystemBrandDefaultsControllerGet } from '@/apis/backend/generated/system-brand-defaults/system-brand-defaults';

/**
 * Returns a `resolve(html)` that fills $brand.* / $auto.* / $product[N].* merge
 * tags against the active brand. Used for on-brand section thumbnails and the
 * editor preview.
 */
export function useBrandResolver(orgId: string, clientId: string) {
  const enabled = !!orgId && !!clientId;
  const profileQ = useBrandsControllerGetClientBrandProfile(orgId, clientId, {
    query: { enabled },
  });
  const defaultsQ = useSystemBrandDefaultsControllerGet({ query: { enabled } });

  const brand = useMemo(
    () => buildBrandResolverContext(profileQ.data, defaultsQ.data),
    [profileQ.data, defaultsQ.data],
  );

  const resolve = useCallback(
    (html: string | null | undefined): string =>
      html ? stripHideIfEmpty(resolveMergeTags(html, brand, undefined)) : '',
    [brand],
  );

  // Same substitution, but WITHOUT the hide-if-empty pass. Editing surfaces
  // address elements by their position among their parent's element children,
  // so a resolver that deletes nodes silently shifts every index after it and
  // an edit lands on the wrong sibling. Callers that map the rendered DOM back
  // to canonical HTML must use this one; callers that only display can use
  // `resolve` and get the truer render.
  const resolveStructural = useCallback(
    (html: string | null | undefined): string =>
      html ? resolveMergeTags(html, brand, undefined) : '',
    [brand],
  );

  // True once the brand queries have settled, so callers can avoid painting a
  // default-color first frame before the real brand loads.
  const ready = !enabled || (!profileQ.isLoading && !defaultsQ.isLoading);

  // `brand` is exposed for callers that need the context itself rather than a
  // resolved string — e.g. compiling a v4 section node to HTML, where the
  // resolver runs inside the compiler instead of over its output.
  return { resolve, resolveStructural, ready, brand };
}

// Drop elements marked data-nm-hide-if-empty whose brand media is absent --
// children before parents, so an empty product strip collapses entirely.
function stripHideIfEmpty(html: string): string {
  if (typeof DOMParser === 'undefined' || html.indexOf('data-nm-hide-if-empty') === -1) return html;
  const doc = new DOMParser().parseFromString(`<div id="nm-root">${html}</div>`, 'text/html');
  const root = doc.getElementById('nm-root');
  if (!root) return html;
  const els = Array.from(root.querySelectorAll('[data-nm-hide-if-empty]'));
  for (let i = els.length - 1; i >= 0; i--) {
    const el = els[i] as HTMLElement;
    const src = el.getAttribute('src');
    const empty =
      el.tagName === 'IMG'
        ? // empty, or an unresolved $product/$brand token (no such brand media)
          !src?.trim() || src.indexOf('$') !== -1
        : !el.textContent?.trim() && !el.querySelector('img[src]');
    if (empty) el.remove();
  }
  return root.innerHTML;
}
